import { useEffect, useReducer, useCallback, useMemo } from 'react';
// utils
import axios, { endpoints } from 'src/utils/axios';
//
import { AuthContext } from './auth-context';
import { isValidToken, setSession } from './utils';
import { ActionMapType, AuthStateType, AuthUserType } from '../../types';
import { IGetUserProfileResponse, IPostLoginResponse } from '../../../interfaces';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

enum Types {
  INITIAL = 'INITIAL',
  LOGIN = 'LOGIN',
  LOGIN_GOOGLE = 'LOGIN_GOOGLE',
  LOGIN_FACEBOOK = 'LOGIN_FACEBOOK',
  REGISTER = 'REGISTER',
  LOGOUT = 'LOGOUT',
  LOADING = 'LOADING',
}

type Payload = {
  [Types.INITIAL]: {
    user: AuthUserType;
  };
  [Types.LOGIN]: {
    user: AuthUserType;
  };
  [Types.LOGIN_GOOGLE]: {
    user: AuthUserType;
  };
  [Types.LOGIN_FACEBOOK]: {
    user: AuthUserType;
  };
  [Types.REGISTER]: {
    user: AuthUserType;
  };
  [Types.LOGOUT]: undefined;
  [Types.LOADING]: {
    loading: boolean;
  };
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

// ----------------------------------------------------------------------

const initialState: AuthStateType = {
  user: null,
  loading: true,
};

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  if (
    action.type === Types.LOGIN ||
    action.type === Types.LOGIN_GOOGLE ||
    action.type === Types.LOGIN_FACEBOOK
  ) {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === Types.REGISTER) {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGOUT) {
    return {
      ...state,
      user: null,
    };
  }
  if (action.type === Types.LOADING) {
    return {
      ...state,
      loading: action.payload.loading,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

const STORAGE_KEY = 'accessToken';

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem(STORAGE_KEY);

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);

        dispatch({
          type: Types.LOADING,
          payload: {
            loading: true,
          },
        });

        const res = await axios.get<IGetUserProfileResponse>(endpoints.auth.me);

        dispatch({
          type: Types.LOADING,
          payload: {
            loading: false,
          },
        });

        const { data } = res.data;

        dispatch({
          type: Types.INITIAL,
          payload: {
            user: {
              ...data,
            },
          },
        });
      } else {
        dispatch({
          type: Types.INITIAL,
          payload: {
            user: null,
          },
        });
      }
    } catch (error) {
      // console.error(error);
      dispatch({
        type: Types.INITIAL,
        payload: {
          user: null,
        },
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(
    async (email: string, password: string) => {
      const data = {
        email,
        password,
      };

      const res = await axios.post<IPostLoginResponse>(endpoints.auth.login, data);

      const { access_token, role } = res.data;

      setSession(access_token);

      dispatch({
        type: Types.LOGIN,
        payload: {
          user: {
            role,
          },
        },
      });

      initialize();

      return role;
    },
    [initialize]
  );

  // LOGIN GOOGLE
  const loginGoogle = useCallback(
    async (data: any) => {
      const res = await axios.get<IPostLoginResponse>(endpoints.auth.googleLoginWithToken, {
        params: data,
      });

      const { access_token, role } = res.data;

      setSession(access_token);

      dispatch({
        type: Types.LOGIN_GOOGLE,
        payload: {
          user: {
            role,
          },
        },
      });

      initialize();

      return role;
    },
    [initialize]
  );

  const loginFacebook = useCallback(
    async (data: any) => {
      const res = await axios.get<IPostLoginResponse>(endpoints.auth.facebookLoginWithToken, {
        params: data,
      });

      const { access_token, role } = res.data;

      setSession(access_token);

      dispatch({
        type: Types.LOGIN_GOOGLE,
        payload: {
          user: {
            role,
          },
        },
      });

      initialize();

      return role;
    },
    [initialize]
  );

  // REGISTER
  const register = useCallback(
    async (email: string, password: string, phoneNo: string) => {
      const data = {
        email,
        password,
        phoneNo,
      };

      const res = await axios.post(endpoints.auth.register, data);

      const { accessToken, role } = res.data;

      localStorage.setItem(STORAGE_KEY, accessToken);

      dispatch({
        type: Types.REGISTER,
        payload: {
          user: {
            role,
          },
        },
      });
      initialize();
    },
    [initialize]
  );

  // LOGOUT
  const logout = useCallback(async () => {
    setSession(null);
    dispatch({
      type: Types.LOGOUT,
    });
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      //
      login,
      loginGoogle,
      loginFacebook,
      register,
      logout,
    }),
    [login, loginFacebook, loginGoogle, logout, register, state.user, status]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
